import { useSiteContent } from "@/context/SiteContentContext";

const indicators = [
  "Proyectos digitales",
  "Sitios web",
  "Sistemas empresariales",
  "Aplicaciones",
  "Automatizaciones",
];

export default function Hero() {
  const { sections } = useSiteContent();
  const hero = sections.hero || {};

  const badge = String(hero.badge || "MARK DIRECT COMPANY · SOLUCIONES DIGITALES");
  const title = String(hero.title || "Transformamos ideas en soluciones digitales.");
  const subtitle = String(
    hero.subtitle ||
      "Desarrollo web, aplicaciones y sistemas digitales diseñados para hacer crecer negocios, automatizar procesos y crear experiencias modernas."
  );
  const btnPrimary = String(hero.btn_primary || "Ver proyectos");
  const btnSecondary = String(hero.btn_secondary || "Crear mi proyecto");

  // Resalta la palabra "ideas" con gradiente si aparece en el título
  const titleParts = title.toLowerCase().includes("ideas")
    ? title.split(/(ideas)/i)
    : null;

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 md:pt-28 pb-16"
    >
      <div className="absolute inset-0 digital-grid" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-primary-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-accent-500/10 blur-3xl" />
      <div className="absolute top-1/3 left-1/2 w-[300px] h-[300px] rounded-full bg-primary-400/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="flex flex-col items-start">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50/60 px-4 py-1.5 text-xs font-semibold tracking-widest text-primary-700 mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-500 glow-pulse" />
              {badge}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.05] text-foreground-950">
              {titleParts
                ? titleParts.map((part, i) =>
                    part.toLowerCase() === "ideas" ? (
                      <span
                        key={i}
                        className="relative inline-block bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent"
                      >
                        {part}
                      </span>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  )
                : title}
            </h1>

            <p className="mt-6 text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl">
              {subtitle}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <a
                href="#proyectos"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3.5 text-sm font-semibold transition-all whitespace-nowrap shadow-[0_0_28px_oklch(var(--primary-500)/0.4)]"
              >
                {btnPrimary}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </a>
              <a
                href="#contacto"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-300 text-primary-700 hover:bg-primary-50 px-7 py-3.5 text-sm font-semibold transition-all whitespace-nowrap"
              >
                {btnSecondary}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-2">
              {indicators.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 rounded-full bg-background-100 border border-background-200 px-3.5 py-1.5 text-xs font-medium text-foreground-600"
                >
                  <i className="ri-checkbox-circle-line text-primary-500 text-sm" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block h-[520px]">
            <div className="absolute top-8 right-0 w-[420px] rounded-2xl glass border border-background-200 shadow-[0_20px_60px_rgba(30,60,120,0.12)] p-5 float-soft">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2.5 h-2.5 rounded-full bg-foreground-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-foreground-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-accent-500" />
                <span className="ml-auto text-xs font-medium text-foreground-500">
                  dashboard.md
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { label: "Proyectos", value: "120+" },
                  { label: "Clientes", value: "80+" },
                  { label: "Uptime", value: "99%" },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl bg-background-100 p-3 border border-background-200"
                  >
                    <p className="text-xl font-bold text-foreground-950">{s.value}</p>
                    <p className="text-[11px] text-foreground-500">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-1.5 h-24 mb-3">
                {[40, 65, 50, 80, 60, 90, 75, 95, 70, 85, 100, 88].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-md bg-gradient-to-t from-primary-500 to-accent-500"
                    style={{ height: `${h}%`, opacity: 0.4 + i * 0.05 }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <span className="w-2 h-2 rounded-full bg-accent-500 glow-pulse" />
                Sistema en línea · Sincronizado
              </div>
            </div>

            <div className="absolute top-0 left-4 w-56 rounded-2xl glass border border-background-200 shadow-[0_12px_40px_rgba(30,60,120,0.12)] p-4 float-soft-delayed">
              <p className="text-[11px] font-medium text-foreground-500 mb-2">deploy.tsx</p>
              <div className="space-y-1.5 font-mono text-[11px]">
                <p className="text-primary-600">const build = () =&gt; {"{"}</p>
                <p className="pl-3 text-foreground-700">idea → diseño</p>
                <p className="pl-3 text-foreground-700">diseño → desarrollo</p>
                <p className="pl-3 text-foreground-700">desarrollo → launch</p>
                <p className="text-primary-600">{"}"}</p>
              </div>
            </div>

            <div className="absolute bottom-8 left-10 w-44 rounded-2xl glass border border-background-200 shadow-[0_12px_40px_rgba(30,60,120,0.12)] p-4 float-soft">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-rocket-line w-4 h-4 flex items-center justify-center" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-foreground-900">Lanzamiento</p>
                  <p className="text-[10px] text-foreground-500">listo</p>
                </div>
              </div>
            </div>

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-40 bg-gradient-to-b from-transparent via-primary-300 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}